/**
 * TierMetaCell.tsx
 * Tier meta-column cell for the Skill Matrix (moved here from Admin → Users in
 * the RBAC audit — Tier is a scheduling attribute, so it lives with the
 * scheduling profile).
 *
 * Tier drives ONLY the Employee Schedule row grouping (Lead / Senior / Tech /
 * Junior headers) — never permissions. When no explicit Tier is stored it is
 * auto-derived from the Job Class (GAL title); admins can override here.
 * Editing requires Lab Admin (PUT /users/:key); everyone else sees text.
 */

import React from 'react';
import { Box, FormControl, MenuItem, Select, Tooltip } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { TIER_OPTIONS, deriveTierFromJobClass } from '../../../../constants/scheduleEnums';
import type { SkillUser } from '../../../../services/scheduleSkillMatrix';

interface TierMetaCellProps {
  user: SkillUser;
  /** True when the caller may edit (Lab Admin). */
  canEdit: boolean;
  onTierChange: (appUserKey: string, tier: string | null) => void;
}

export const TierMetaCell: React.FC<TierMetaCellProps> = ({ user, canEdit, onTierChange }) => {
  const theme = useTheme();
  const derivedTier = deriveTierFromJobClass(user.JobClass);

  if (!canEdit) {
    if (user.Tier) return <>{user.Tier}</>;
    if (derivedTier) {
      return (
        <Tooltip title="Auto-derived from Job Class">
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
            <AutoAwesome sx={{ fontSize: 12 }} />
            {derivedTier}
          </Box>
        </Tooltip>
      );
    }
    return <em style={{ color: theme.schedule.noActivity }}>Unassigned</em>;
  }

  return (
    <FormControl size="small" sx={{ minWidth: 96 }}>
      <Select
        displayEmpty
        value={user.Tier ?? ''}
        onChange={(e) => onTierChange(user.AppUserKey, e.target.value || null)}
        renderValue={(v) => (v
          ? <>{v}</>
          : derivedTier
            ? (
              <Tooltip title="Auto-derived from Job Class — pick a value to override">
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, color: theme.palette.text.disabled }}>
                  <AutoAwesome sx={{ fontSize: 12 }} />
                  {derivedTier}
                </Box>
              </Tooltip>
            )
            : <Box component="span" sx={{ color: theme.palette.text.disabled }}>Tier</Box>
        )}
        sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.3, pl: 1 } }}
      >
        <MenuItem value="" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {derivedTier ? `Auto (${derivedTier})` : '— None —'}
        </MenuItem>
        {TIER_OPTIONS.map((t) => (
          <MenuItem key={t} value={t} sx={{ fontSize: '0.75rem' }}>{t}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
